import axios from 'axios';
import React from 'react';
import type { User } from '@/App.interfaces';

export default function useFeatureFlags(user?: User | null) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [featureFlags, setFeatureFlags] = React.useState<
    Record<string, boolean>
  >({});

  const upsertFeatureFlag = React.useCallback(
    async (feature: string, isEnabled: boolean) => {
      try {
        setIsLoading(true);
        if (!window.ReactAppEnv.api.featureFlags || !user?.token) {
          console.error('Feature flags API URL or user token is missing.');
          return;
        }
        const response = await axios.post<Record<string, boolean>>(
          '/upsert?app=sgapp-client',
          {
            feature,
            isEnabled,
          },
          {
            baseURL: window.ReactAppEnv.api.featureFlags,
            headers: {
              'x-authorization': `Bearer ${user.token}`,
            },
          },
        );
        setFeatureFlags((prev) => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error('Error upserting feature flag:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.token],
  );

  React.useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        setIsLoading(true);
        if (window.ReactAppEnv.api.featureFlags) {
          const response = await axios.get<Record<string, boolean>>(
            '/list?app=sgapp-client',
            {
              baseURL: window.ReactAppEnv.api.featureFlags,
            },
          );
          setFeatureFlags(response.data);
        }
      } catch (error) {
        console.error('Error fetching feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatureFlags();
  }, []);

  return { isLoading, featureFlags, upsertFeatureFlag };
}
